import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useTrace } from '../context/TraceContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  BarChart3, TrendingUp, TrendingDown, FileText
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';

const PQRPage = () => {
  const { pidTraceMode } = useTrace();

  const qualityTrendData = [
    { month: 'Jul', batches: 45, passed: 44, deviations: 3 },
    { month: 'Aug', batches: 52, passed: 51, deviations: 4 },
    { month: 'Sep', batches: 48, passed: 47, deviations: 2 },
    { month: 'Oct', batches: 50, passed: 49, deviations: 5 },
    { month: 'Nov', batches: 55, passed: 54, deviations: 3 },
    { month: 'Dec', batches: 47, passed: 46, deviations: 4 }
  ];

  const capaEffectivenessData = [
    { month: 'Jul', closed: 8, effective: 7 },
    { month: 'Aug', closed: 12, effective: 11 },
    { month: 'Sep', closed: 6, effective: 6 },
    { month: 'Oct', closed: 10, effective: 9 },
    { month: 'Nov', closed: 9, effective: 8 },
    { month: 'Dec', closed: 7, effective: 7 }
  ];

  return (
    <div className="space-y-6" data-testid="pqr-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Product Quality Review
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Annual/rolling PQR data and management dashboard
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
            FY 2025-26 Rolling
          </Badge>
          {/* <ReportExportButton 
            title="Product Quality Review Report"
            subtitle="Annual/rolling PQR data and management dashboard"
          /> */}
        </div>
      </div>

      <ReportSection id="pqr-metrics" title="PQR Metrics" description="Product Quality Review key indicators">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.14" req="SM-PQR-001" />
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Total Batches (6M)</p>
              <p className="text-2xl font-bold text-slate-900">297</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Pass Rate</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-emerald-600">98.3%</p>
                <TrendingUp className="h-4 w-4 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Deviations</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-amber-600">21</p>
                <TrendingDown className="h-4 w-4 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">CAPA Effectiveness</p>
              <p className="text-2xl font-bold text-emerald-600">92%</p>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <ReportSection id="pqr-charts" title="PQR Charts" description="Quality trends and CAPA effectiveness">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §11" req="SM-PQR-001" />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Batch Production Trend</CardTitle>
            </CardHeader>
            <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={qualityTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Bar dataKey="passed" fill="#10b981" radius={[4, 4, 0, 0]} name="Passed" />
                  <Bar dataKey="deviations" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Deviations" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §11" req="SM-CAPA-001" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">CAPA Effectiveness Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={capaEffectivenessData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Line type="monotone" dataKey="closed" stroke="#3b82f6" strokeWidth={2} name="Closed" />
                  <Line type="monotone" dataKey="effective" stroke="#10b981" strokeWidth={2} name="Effective" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      </ReportSection>

      <Card className="bg-blue-50 border border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">PQR Report Generation</p>
              <p className="text-xs text-blue-700 mt-1">
                Full PQR reports can be generated from this dashboard for regulatory submissions. 
                Reports include batch analysis, deviation trends, CAPA effectiveness, and stability data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PQRPage;
